/**
 * Theme management hook.
 *
 * Supports three modes: 'system' (follows OS preference), 'light', 'dark'.
 * Persists choice to localStorage. Sets data-theme on <html> for CSS variable switching.
 */

import { useState, useEffect, useCallback } from 'preact/hooks';

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'pxtxt-theme';

function getStoredPreference(): ThemePreference {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
	} catch {}
	return 'system';
}

function getSystemTheme(): ResolvedTheme {
	if (typeof window === 'undefined') return 'light';
	return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(preference: ThemePreference) {
	const root = document.documentElement;
	if (preference === 'system') {
		root.removeAttribute('data-theme');
	} else {
		root.setAttribute('data-theme', preference);
	}
}

export function useTheme() {
	const [preference, setPreferenceState] = useState<ThemePreference>(getStoredPreference);
	const [resolved, setResolved] = useState<ResolvedTheme>(() => {
		const pref = getStoredPreference();
		return pref === 'system' ? getSystemTheme() : pref;
	});

	const setPreference = useCallback((pref: ThemePreference) => {
		setPreferenceState(pref);
		try { localStorage.setItem(STORAGE_KEY, pref); } catch {}
		applyTheme(pref);
		setResolved(pref === 'system' ? getSystemTheme() : pref);
	}, []);

	// Listen for system theme changes
	useEffect(() => {
		const mq = window.matchMedia('(prefers-color-scheme: dark)');
		const handler = () => {
			if (getStoredPreference() === 'system') {
				setResolved(getSystemTheme());
			}
		};
		mq.addEventListener('change', handler);
		return () => mq.removeEventListener('change', handler);
	}, []);

	// Apply on mount
	useEffect(() => {
		applyTheme(preference);
	}, []);

	return { preference, resolved, setPreference };
}
